import React, { useState, useEffect } from 'react';
import { getAxiosErrorMessage } from '../utils/errorUtils';
import { 
  Plus, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  Key,
  Save,
  X,
  Edit,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { variablesService } from '../services/api';
import { Variable } from '../types';

const VariablesTab: React.FC = () => {
  const [variables, setVariables] = useState<Variable[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [newVariables, setNewVariables] = useState<{ key: string; value: string }[]>([]);
  const [showValues, setShowValues] = useState<{ [key: string]: boolean }>({});
  const [editingVariable, setEditingVariable] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadVariables();
  }, []);

  const loadVariables = async () => {
    try {
      const data = await variablesService.getAll();
      setVariables(data);
    } catch (_error) {
      toast.error('Fehler beim Laden der Variablen');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewVariable = () => {
    setNewVariables([...newVariables, { key: '', value: '' }]);
  };

  const handleRemoveNewVariable = (index: number) => {
    setNewVariables(newVariables.filter((_, i) => i !== index));
  };

  const handleNewVariableChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...newVariables];
    updated[index][field] = value;
    setNewVariables(updated);
  };

  const handleSaveVariables = async () => {
    try {
      await variablesService.createMultiple(newVariables);
      toast.success('Variablen erfolgreich gespeichert');
      setNewVariables([]);
      loadVariables();
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Speichern der Variablen'));
    }
  };

  const handleDeleteVariable = async (id: string, key: string) => {
    if (!confirm(`Möchten Sie die Variable "${key}" wirklich löschen?`)) {
      return;
    }

    setDeleting(id);
    try {
      await variablesService.delete(id);
      toast.success('Variable erfolgreich gelöscht');
      setVariables(variables.filter(v => v.id !== id));
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Löschen der Variable'));
    } finally {
      setDeleting(null);
    }
  };

  const toggleValueVisibility = (key: string) => {
    setShowValues(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleEditVariable = (variable: Variable) => {
    setEditingVariable(variable.id);
    setEditValue(variable.value);
  };

  const handleSaveEdit = async (variable: Variable) => {
    if (!editValue.trim()) {
      toast.error('Wert darf nicht leer sein');
      return;
    }

    setSaving(true);
    try {
      await variablesService.update(variable.id, variable.key, editValue);
      toast.success('Variable erfolgreich aktualisiert');
      setEditingVariable(null);
      setEditValue('');
      loadVariables();
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Aktualisieren der Variable'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingVariable(null);
    setEditValue('');
  };

  const handleSyncMissingVariables = async () => {
    setSyncing(true);
    try {
      const result = await variablesService.syncMissingToFunctions();
      if (result.updated > 0) {
        const details = Object.entries(result.addedPerFunction)
          .map(([name, count]) => `${name}: ${count}`)
          .join(', ');
        toast.success(`Fehlende Variablen zu ${result.updated} Function(s) hinzugefügt. ${details}`);
      } else {
        toast.success('Alle Functions haben bereits alle Variablen.');
      }
    } catch (error: unknown) {
      toast.error(getAxiosErrorMessage(error, 'Fehler beim Syncen der fehlenden Variablen'));
    } finally {
      setSyncing(false);
    }
  };

  const filteredVariables = variables.filter(variable =>
    variable.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ 
          width: '32px', 
          height: '32px', 
          border: '3px solid #f3f4f6', 
          borderTop: '3px solid #2563eb', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>
          Edge Function Variables
        </h2>
        <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
          Verwalten Sie die Variablen für Ihre Edge Functions.
        </p>
      </div>

      {/* Add New Variables Section */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '24px' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>ADD NEW VARIABLES</h3>
        </div>
        
        <div style={{ padding: '24px' }}>
          {newVariables.map((variable, index) => (
            <div key={index} style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="e.g. CLIENT_KEY"
                  value={variable.key}
                  onChange={(e) => handleNewVariableChange(index, 'key', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              <div style={{ flex: 2, position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Enter value..."
                  value={variable.value}
                  onChange={(e) => handleNewVariableChange(index, 'value', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                />
                <button
                  onClick={() => handleRemoveNewVariable(index)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '4px',
                    color: '#9ca3af',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                  title="Variable entfernen"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={handleAddNewVariable}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                cursor: 'pointer'
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add another
            </button>
            
            <button
              onClick={handleSaveVariables}
              disabled={newVariables.length === 0 || newVariables.some(v => !v.key.trim())}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 20px',
                backgroundColor: '#16a34a',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                opacity: (newVariables.length === 0 || newVariables.some(v => !v.key.trim())) ? 0.5 : 1
              }}
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative' }}>
          <Search className="h-4 w-4" style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: '#9ca3af' 
          }} />
          <input
            type="text"
            placeholder="Search for a variable"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white'
            }}
          />
        </div>
      </div>

      {/* Variables Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>VARIABLES</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={handleSyncMissingVariables}
                disabled={syncing}
                title="Fehlende Variablen zu allen Function .env-Dateien hinzufügen (bestehende Einträge bleiben unverändert)"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#eff6ff',
                  color: '#2563eb',
                  border: '1px solid #dbeafe',
                  borderRadius: '6px',
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: syncing ? 0.6 : 1
                }}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sync...' : 'Fehlende Variablen syncen'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ padding: '0' }}>
          {filteredVariables.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <Key className="h-16 w-16 mx-auto mb-4" style={{ color: '#d1d5db' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
                {searchTerm ? 'Keine Variablen gefunden' : 'Keine Variablen vorhanden'}
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff.' : 'Fügen Sie Ihre erste Variable hinzu.'}
              </p>
            </div>
          ) : (
            filteredVariables.map((variable, index) => (
              <div
                key={variable.id}
                style={{
                  padding: '20px 24px',
                  borderBottom: index < filteredVariables.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'white'
                }}
              >
                {/* Variable Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      backgroundColor: '#fef3c7', 
                      borderRadius: '8px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      marginRight: '12px'
                    }}>
                      <Key className="h-4 w-4" style={{ color: '#d97706' }} />
                    </div>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
                      {variable.key}
                    </h4>
                  </div>
                  
                  {/* Value */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    {editingVariable === variable.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'monospace'
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveEdit(variable)}
                          disabled={saving}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: saving ? '#9ca3af' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            opacity: saving ? 0.6 : 1
                          }}
                          title={saving ? 'Speichert...' : 'Speichern'}
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                          title="Abbrechen"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span style={{ 
                          fontSize: '14px', 
                          color: '#6b7280', 
                          fontFamily: 'monospace',
                          marginRight: '8px',
                          wordBreak: 'break-all'
                        }}>
                          {showValues[variable.key] ? variable.value : '••••••••••••••••'}
                        </span>
                        <button
                          onClick={() => toggleValueVisibility(variable.key)}
                          style={{ 
                            padding: '4px', 
                            color: '#9ca3af',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '4px'
                          }}
                          title={showValues[variable.key] ? 'Wert verbergen' : 'Wert anzeigen'}
                        >
                          {showValues[variable.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Metadata */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#6b7280' }}>
                      <span style={{ fontFamily: 'monospace' }}>
                        {variable.value.substring(0, 16)}...
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#6b7280' }}>
                      <span>
                        {formatDistanceToNow(new Date(variable.updated_at), {
                          addSuffix: true,
                          locale: de,
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {editingVariable !== variable.id && (
                    <button
                      onClick={() => handleEditVariable(variable)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        backgroundColor: '#f0f9ff',
                        color: '#0ea5e9',
                        borderRadius: '6px',
                        border: '1px solid #bae6fd',
                        cursor: 'pointer'
                      }}
                      title="Variable bearbeiten"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteVariable(variable.id, variable.key)}
                    disabled={deleting === variable.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      backgroundColor: deleting === variable.id ? '#9ca3af' : '#fef2f2',
                      color: deleting === variable.id ? '#ffffff' : '#dc2626',
                      borderRadius: '6px',
                      border: deleting === variable.id ? '1px solid #9ca3af' : '1px solid #fecaca',
                      cursor: deleting === variable.id ? 'not-allowed' : 'pointer',
                      opacity: deleting === variable.id ? 0.6 : 1
                    }}
                    title={deleting === variable.id ? 'Wird gelöscht...' : 'Variable löschen'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default VariablesTab;
